/**
 * @fileoverview Servizio per rating (classifiche) per gruppo/periodo.
 */
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS, RATINGS_FIELDS, ELO_CONFIG } from '../domain/dataModel';

/**
 * Recupera tutti i rating del gruppo per il periodo indicato.
 * @param {string} groupId
 * @param {string} periodId
 * @returns {Promise<Array<{ id: string, ...Object }>>}
 */
export async function getRatingsByPeriod(groupId, periodId) {
  try {
    if (!groupId || !periodId) return [];
    const q = query(
      collection(db, COLLECTIONS.RATINGS),
      where(RATINGS_FIELDS.GROUP_ID, '==', groupId),
      where(RATINGS_FIELDS.PERIOD_ID, '==', periodId)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento dei rating.');
  }
}

/**
 * Recupera i rating attivi (isActive !== false) per gruppo e periodo.
 * @param {string} groupId
 * @param {string} periodId
 * @returns {Promise<Array<{ id: string, ...Object }>>}
 */
export async function getActiveRatings(groupId, periodId) {
  try {
    const all = await getRatingsByPeriod(groupId, periodId);
    return all.filter((r) => r[RATINGS_FIELDS.IS_ACTIVE] !== false);
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento dei rating attivi.');
  }
}

/**
 * Aggiorna un documento rating (campi parziali).
 * @param {string} ratingId
 * @param {Object} data - Campi da aggiornare
 * @returns {Promise<void>}
 */
export async function updateRating(ratingId, data) {
  try {
    if (!ratingId) throw new Error('ID rating mancante.');
    const ref = doc(db, COLLECTIONS.RATINGS, ratingId);
    await updateDoc(ref, {
      ...data,
      [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
  } catch (err) {
    throw new Error(err?.message || 'Errore nell\'aggiornamento del rating.');
  }
}

/**
 * Riattiva il rating del membro per il periodo: imposta isActive true o crea il documento se non esiste.
 * @param {string} memberId - ID documento groupMembers (per guest o per identificare il membro)
 * @param {string|null} userId - UID se membro utente, null se guest
 * @param {string} periodId
 * @param {string} groupId
 * @returns {Promise<void>}
 */
export async function reactivateRating(memberId, userId, periodId, groupId) {
  try {
    if (!groupId || !periodId) throw new Error('Gruppo o periodo mancante.');
    const isGuest = userId == null || userId === '';
    const q = query(
      collection(db, COLLECTIONS.RATINGS),
      where(RATINGS_FIELDS.GROUP_ID, '==', groupId),
      where(RATINGS_FIELDS.PERIOD_ID, '==', periodId),
      where(isGuest ? RATINGS_FIELDS.MEMBER_ID : RATINGS_FIELDS.USER_ID, '==', isGuest ? memberId : userId)
    );
    const snap = await getDocs(q);
    if (snap.docs.length > 0) {
      await Promise.all(
        snap.docs.map((d) =>
          updateDoc(d.ref, {
            [RATINGS_FIELDS.IS_ACTIVE]: true,
            [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp(),
          })
        )
      );
    } else {
      await createRating(groupId, periodId, isGuest ? memberId : null, isGuest ? null : userId, ELO_CONFIG.RATING_BASE);
    }
  } catch (err) {
    throw new Error(err?.message || 'Errore nella riattivazione del rating.');
  }
}

/**
 * Disattiva il rating del membro per il periodo (isActive false).
 * @param {string} memberId - ID groupMembers (usato per guest; per utente si usa comunque per coerenza)
 * @param {string|null} userId - UID se membro utente, null se guest
 * @param {string} periodId
 * @param {string} groupId
 * @returns {Promise<void>}
 */
export async function deactivateRating(memberId, userId, periodId, groupId) {
  try {
    if (!groupId || !periodId) throw new Error('Gruppo o periodo mancante.');
    const isGuest = userId == null || userId === '';
    const q = query(
      collection(db, COLLECTIONS.RATINGS),
      where(RATINGS_FIELDS.GROUP_ID, '==', groupId),
      where(RATINGS_FIELDS.PERIOD_ID, '==', periodId),
      where(isGuest ? RATINGS_FIELDS.MEMBER_ID : RATINGS_FIELDS.USER_ID, '==', isGuest ? memberId : userId)
    );
    const snap = await getDocs(q);
    await Promise.all(
      snap.docs.map((d) =>
        updateDoc(d.ref, {
          [RATINGS_FIELDS.IS_ACTIVE]: false,
          [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp(),
        })
      )
    );
  } catch (err) {
    throw new Error(err?.message || 'Errore nella disattivazione del rating.');
  }
}

/**
 * Crea un nuovo documento rating (membro o ospite).
 * @param {string} groupId
 * @param {string} periodId
 * @param {string|null} memberId - ID groupMembers per ospite; null per utente
 * @param {string|null} userId - UID per utente; null per ospite
 * @param {number} [initialRating] - Rating ELO iniziale (default da ELO_CONFIG)
 * @returns {Promise<{ ratingId: string }>}
 */
export async function createRating(groupId, periodId, memberId, userId, initialRating = ELO_CONFIG.RATING_BASE) {
  try {
    if (!groupId || !periodId) throw new Error('Gruppo o periodo mancante.');
    const points = Math.min(3000, Math.max(0, Number(initialRating) || ELO_CONFIG.RATING_BASE));
    const payload = {
      [RATINGS_FIELDS.GROUP_ID]: groupId,
      [RATINGS_FIELDS.PERIOD_ID]: periodId,
      [RATINGS_FIELDS.RATING]: points,
      [RATINGS_FIELDS.MATCHES_PLAYED]: 0,
      [RATINGS_FIELDS.WINS]: 0,
      [RATINGS_FIELDS.LOSSES]: 0,
      [RATINGS_FIELDS.IS_ACTIVE]: true,
      [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp(),
    };
    if (memberId != null && memberId !== '') {
      payload[RATINGS_FIELDS.MEMBER_ID] = memberId;
    }
    if (userId != null && userId !== '') {
      payload[RATINGS_FIELDS.USER_ID] = userId;
    }
    const ref = await addDoc(collection(db, COLLECTIONS.RATINGS), payload);
    return { ratingId: ref.id };
  } catch (err) {
    throw new Error(err?.message || 'Errore nella creazione del rating.');
  }
}
